// Minimal fetch wrapper for the hq-controlplane HTTP API.
//
// Identity propagation is now uniform across real OAuth and dev:
// both mint the same sealed `hq-session` cookie (real OAuth via
// /v1/auth/slack/callback, dev via /v1/auth/dev/continue). A
// server-side middleware unseals the cookie and fills in the
// X-Hq-* headers downstream handlers consume. The browser just
// calls `fetch(...)` with `credentials: 'include'` and the cookie
// rides along - same path for XHR, direct nav (download anchors),
// image src embeds, share links. The old sessionStorage header stash
// is gone because direct navigations never carried it.
//
// All calls go through the Vite dev-server proxy. See `vite.config.ts`.
//
// Workspace scoping: the active workspace lives in the URL (`/w/<slug>/`). Every
// call carries it as the `X-Hq-Ws` hint so the controlplane resolves identity in
// THAT workspace (validated against the session's memberships server-side - see
// auth_session::resolve_cookie_workspace). `X-Hq-Ws` is a client *hint*, never a
// server-trusted identity header; the sealed cookie remains the only proof of
// who the caller is. Non-fetch navigations that can't set a header (downloads,
// image embeds) carry `?ws=<slug>` instead - see lib/workspace + wsParam below.

import { currentWorkspace } from '@/lib/workspace';

interface ApiError {
  status: number;
  error: string;
}

async function send<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const ws = currentWorkspace();
  if (ws) headers['X-Hq-Ws'] = ws;
  // Per-call headers (e.g. Idempotency-Key for retry-safe mutations). Applied
  // after the defaults so a caller can't accidentally clobber X-Hq-Ws here.
  if (extraHeaders) for (const [k, v] of Object.entries(extraHeaders)) headers[k] = v;
  const init: RequestInit = {
    method,
    // `include` sends the sealed hq-session cookie. Same cookie
    // direct navigations carry, so identity is consistent across
    // every path the browser uses to fetch from the controlplane.
    credentials: 'include',
    headers,
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  const res = await fetch(path, init);
  if (!res.ok) {
    let err: ApiError;
    try {
      err = { status: res.status, ...(await res.json()) };
    } catch {
      err = { status: res.status, error: res.statusText };
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get:    <T>(path: string)                 => send<T>('GET',    path),
  post:   <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
            send<T>('POST', path, body, headers),
  put:    <T>(path: string, body?: unknown) => send<T>('PUT',    path, body),
  patch:  <T>(path: string, body?: unknown) => send<T>('PATCH',  path, body),
  delete: <T>(path: string)                 => send<T>('DELETE', path),
};
